import type {
  CostUnit,
  QueryCostDataRequest,
  Condition,
  CostDataGroupBy,
  CostGranularity,
  UsageUnit,
} from '@repo/api-gw-sdk';
import { ConditionOperatorEnum } from '@repo/api-gw-sdk';

import { OPERATORS } from '@/components/conditions/OperatorSelect';
import {
  collectValueConditions,
  PropertyPolicyRelation,
  type FilterProperty,
} from '@/types/advanceFilter';
import type { Dayjs } from '@/utils/dayjs';

export const createQueryCostDataRequest = (
  dateRange: {
    startDate: Dayjs;
    endDate: Dayjs;
  },
  groupByField: CostDataGroupBy,
  granularity: CostGranularity,
  query: Condition | undefined,
  costUnit: CostUnit,
  usageUnit: UsageUnit
): QueryCostDataRequest => ({
  timeFrame: {
    startTime: dateRange.startDate.toDate(),
    endTime: dateRange.endDate.toDate(),
  },
  groupBy: groupByField,
  granularity,
  filters: convertQuery(query),
  costUnit,
  usageUnit,
});

export const convertQuery = (
  rootCondition: Condition | undefined
): Record<string, unknown> | undefined => {
  if (!rootCondition?.conditions?.length) {
    return;
  }

  const conditions = collectValueConditions(rootCondition);
  return conditions.reduce<Record<string, unknown>>((acc, condition) => {
    acc[condition.property!] = {
      [convertOperator(condition.operator)]: condition.value,
    };
    return acc;
  }, {});
};

const convertOperator = (operator: string): string => {
  if (operator === ConditionOperatorEnum.In) {
    return '_in';
  }

  if (operator === ConditionOperatorEnum.ContainsAnyOf) {
    return 'containsAnyOf';
  }

  return operator;
};

export const createEmptyCondition = (
  property: FilterProperty,
  value?: string
) => {
  const condition = {
    property: property.name,
    operator: Object.keys(
      OPERATORS[
        property.policyRelation ?? PropertyPolicyRelation.MultipleValues
      ]
    )?.[0],
    value: [] as string[],
  };

  if (value) {
    condition.value.push(value);
  }

  return condition;
};
